import { MetricCard } from './ui/MetricCard'
import { FunnelData } from '@/hooks/useStudioAnalytics'
import { formatNumber, formatPercentage } from '@/lib/utils'
import { calculateConversion, getConversionColor } from '@/lib/analytics'
import { TrendingUp, ArrowRight } from 'lucide-react'

export interface ConversionsCardProps {
  /** Данные воронки */
  funnelData: FunnelData
  /** Плановые конверсии (опционально) */
  planConversions?: {
    cr1Plan: number // Leads -> Appointments
    cr2Plan: number // Appointments -> Visits
    cr3Plan: number // Visits -> Sales
  }
  /**
   * Звонки за ТОТ ЖЕ период, что и воронка (`calls_count` / `answered_calls_count`).
   * Считается одной точкой в `AnalyticsKpiGrid` — второй реализации не заводить,
   * иначе экран студии и сводный экран разойдутся.
   */
  callsData?: {
    calls: number
    answered: number
  }
}

/**
 * Компонент отображения конверсий
 * Показывает общую конверсию и детальную разбивку по этапам воронки
 */
export function ConversionsCard({ funnelData, planConversions, callsData }: ConversionsCardProps) {
  const { leads, appointments, visits, sales } = funnelData

  // Рассчитываем фактические конверсии
  const cr1 = calculateConversion(appointments, leads) // Leads -> Appointments (запись)
  const cr2 = calculateConversion(visits, appointments) // Appointments -> Visits (приход)
  const cr3 = calculateConversion(sales, visits) // Visits -> Sales (покупка)
  const crTotal = calculateConversion(sales, leads) // Leads -> Sales (общая)

  // Плановые конверсии
  const cr1Plan = planConversions?.cr1Plan || 0
  const cr2Plan = planConversions?.cr2Plan || 0
  const cr3Plan = planConversions?.cr3Plan || 0
  const crTotalPlan = cr1Plan && cr2Plan && cr3Plan ? (cr1Plan * cr2Plan * cr3Plan) / 10000 : 0

  // ⚠️ Дозвон — доля РЕЗУЛЬТАТИВНЫХ ЗВОНКОВ, а не этап воронки: у него другая база.
  // Замер PROD 10.09.2026 за 90 дней: звонков в 12 раз больше, чем заявок, а дозвонов
  // больше, чем заявок, у 116 студий из 140 — то есть цепочку «заявка → дозвон → запись»
  // эти данные не образуют. Поэтому число стоит отдельно и подписано своей базой
  // («звонок-дозвон», зеркало «заявка-покупка»), а не встраивается в ряд со стрелками.
  // Плана по звонкам нет ни в одной таблице (`studio_weekly_plan` их не содержит),
  // поэтому плановой строки под числом не будет никогда, а цвет — нейтральный.
  // ⚠️ Сравнивать студии по этому числу и красить его нельзя: в нём смешаны ДВА процесса —
  // обзвон горячих заявок и прозвон холодной базы. Замер: до 10 звонков на заявку (35 студий)
  // дают медиану дозвона 31,5 %, от 30 звонков (34 студии) — 17,0 %. Цвет или план сделали бы
  // отстающей студию, которая просто взялась за базу. Разбор — docs/ANSWERED_CALLS_METRIC_REVIEW_2026-09-10.md.
  // ⚠️ Пустые дозвоны при непустых звонках — это «не заполняют», а не «не дозвонились»
  // (40 студий из 180 за 90 дней), поэтому 0 % не рисуем вовсе: на сводном экране
  // отличить незаполненное от настоящего нуля нечем — RPC агрегирует NULL в 0.
  const answeredShare = callsData && callsData.calls > 0 && callsData.answered > 0
    ? calculateConversion(callsData.answered, callsData.calls)
    : null

  return (
    <MetricCard
      icon={TrendingUp}
      iconColor="text-info"
      title="Конверсии"
      value=""
    >
      {/* Детальные конверсии по этапам */}
      <div className="mt-3 flex items-center justify-center gap-5 text-sm">
        {/* CR1: Запись */}
        <ConversionStage
          value={cr1}
          plan={cr1Plan}
          label="запись"
        />

        <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />

        {/* CR2: Приход */}
        <ConversionStage
          value={cr2}
          plan={cr2Plan}
          label="приход"
        />

        <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />

        {/* CR3: Покупка */}
        <ConversionStage
          value={cr3}
          plan={cr3Plan}
          label="покупка"
        />
      </div>

      {/*
        Итоговая строка: слева дозвон (вход, до воронки), справа сквозная конверсия (итог воронки).
        Оба числа стоят ПОД рядом этапов, а не в шапке: пока сквозная конверсия была бейджем,
        она задавала высоту шапки, и вместе с отдельной строкой дозвона карточка вырастала
        на две полосы. Кегль у обоих чисел одинаковый — это одна строка итогов.
        ⚠️ Оба блока выровнены ПО БАЗОВОЙ ЛИНИИ (`items-baseline` и снаружи, и внутри каждого):
        при `items-start` подпись прижимается к верху числа, а не стоит на его базовой линии,
        и «звонок-дозвон» с «заявка-покупка» оказываются на разной высоте.
      */}
      <div className="mt-3 flex items-baseline gap-3">
        {answeredShare !== null && callsData && (
          <div
            className="flex items-baseline gap-2"
            title={`Дозвонились ${formatNumber(callsData.answered)} из ${formatNumber(callsData.calls)} звонков`}
          >
            <p className={`text-xl font-bold leading-none ${getConversionColor(answeredShare, 0)}`}>
              {formatPercentage(answeredShare)}
            </p>
            <span className="text-xs text-muted-foreground">звонок-дозвон</span>
          </div>
        )}

        <div className="ml-auto flex items-baseline gap-2">
          <div className="flex min-w-[68px] flex-col items-center">
            <p className={`text-xl font-bold leading-none ${getConversionColor(crTotal, crTotalPlan)}`}>
              {formatPercentage(crTotal)}
            </p>
            {crTotalPlan > 0 && (
              <span className="mt-2 text-[11px] font-medium leading-none text-slate-400">
                {formatPercentage(crTotalPlan)}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">заявка-покупка</p>
        </div>
      </div>
    </MetricCard>
  )
}

/**
 * Компонент отображения одного этапа конверсии
 */
interface ConversionStageProps {
  value: number
  plan: number
  label: string
}

function ConversionStage({ value, plan, label }: ConversionStageProps) {
  const color = getConversionColor(value, plan)

  return (
    <div className="flex min-w-[72px] flex-col items-center">
      <p className={`text-xl font-bold leading-none ${color}`}>
        {formatPercentage(value)}
      </p>
      <p className="mt-0.5 text-[11px] leading-none text-muted-foreground">{label}</p>
      {plan > 0 && (
        <span className="mt-1 text-[11px] font-medium leading-none text-slate-400">
          {formatPercentage(plan)}
        </span>
      )}
    </div>
  )
}
